/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to customer operations.
 * The page should feel calm, human, and controlled, giving users a visible manual path
 * for billing, plan changes, report issues, broken links, and service requests.
 */

import { useAuth } from "@/_core/hooks/useAuth";
import PolicyLayout from "@/components/PolicyLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/trpc";
import { CreditCard, Headphones, LifeBuoy, MailCheck, ShieldAlert, Sparkles } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";

const cards = [
  {
    title: "Manual support path",
    body: "This page exists so users can find a visible route for billing, report-delivery, privacy, and account requests even before every workflow is fully self-served inside the product.",
    icon: Headphones,
  },
  {
    title: "Billing and plan handling",
    body: "Upgrade, downgrade, cancellation, subscription clarification, and payment-history questions may be handled manually after identity verification and order review.",
    icon: CreditCard,
  },
  {
    title: "Operational issue reporting",
    body: "Broken links, inaccessible pages, crash reports, suspicious activity, and delivery failures should be reported promptly so the service surface can be corrected before they create further user harm.",
    icon: LifeBuoy,
  },
];

const sections = [
  {
    id: "how-to-contact",
    title: "What support is for",
    intro:
      "The support path is the public operating channel for customers who need human review of account, report, billing, privacy, or service-integrity issues.",
    paragraphs: [
      "Support may be used for subscription questions, upgrade or downgrade requests, cancellation requests, payment clarifications, invoice follow-up, report-delivery issues, account-access problems, privacy requests, suspicious activity reports, and general website or application issues that materially affect the customer journey.",
      "To reduce risk of unauthorized changes, Command Secrets may ask for enough information to verify that the requester is the rightful account holder or authorized payer before discussing protected account details, changing billing status, or reissuing analytical content.",
      "Customers can use the structured intake flow on this page to submit a clear support brief into the review queue. A concise, complete request reduces avoidable back-and-forth and helps the service resolve issues faster.",
    ],
  },
  {
    id: "billing",
    title: "Billing, upgrades, downgrades, and plan corrections",
    intro:
      "Billing support is intended to make plan administration visible and controlled, not hidden or guess-based.",
    paragraphs: [
      "If you need to upgrade, downgrade, cancel, correct a payment issue, confirm a charge, or review which plan is attached to your account, use the support path and include enough information for verification. Command Secrets may process plan changes immediately, at the next renewal point, or after a manual review depending on what has already been billed or delivered.",
      "Where additional analytical work, new reports, or premium outputs are triggered by an upgrade, those outputs may become subject to the rendered-analysis no-refund rule once created or made available. Where a downgrade is requested, access to higher-tier features may remain available until the current paid period ends unless a different arrangement is confirmed in writing.",
      "If a customer believes a charge was duplicated, technically misprocessed, or otherwise incorrect, Command Secrets may request transaction references, timestamps, and account identifiers before determining whether a billing correction is appropriate.",
    ],
  },
  {
    id: "reports-and-delivery",
    title: "Report delivery, watermarking, and access restoration",
    intro:
      "Analytical outputs are protected deliverables and should be handled through a controlled delivery trail.",
    paragraphs: [
      "If a paid report was expected but not received, if a download link failed, or if a delivered file appears damaged or inaccessible, customers should report the issue promptly through support. Command Secrets may verify order status and resend the output through an appropriate controlled method.",
      "Reports may include watermarking, branding, access references, or other protective measures intended to preserve authorship and discourage unauthorized redistribution of proprietary analytical work. These protections are part of the product's intellectual-property posture and do not by themselves entitle a user to a refund or replacement.",
      "Customers remain responsible for safeguarding files and account emails after lawful delivery. If a user shares or forwards a report to another person, that onward disclosure is the user's own responsibility unless Command Secrets has explicitly agreed otherwise.",
    ],
  },
  {
    id: "technical-issues",
    title: "Broken links, crash prevention, and service integrity issues",
    intro:
      "A serious digital service should give users a path to report faults before trust is lost.",
    paragraphs: [
      "If you encounter broken links, inaccessible pages, repetitive navigation loops, failed buttons, loading errors, crashes, or inconsistent report-delivery behaviour, please report the issue with the page address, device type, browser, approximate time, and a short description of what happened immediately before the problem occurred. This information helps isolate the fault more reliably.",
      "Command Secrets aims to monitor for technical faults and maintain a service surface that is stable, readable, and commercially credible. However, no website or application is immune from temporary defects, third-party outages, or unexpected regressions, so user reports remain an important part of service integrity and anti-crash improvement.",
      "If an issue affects security, account control, suspicious scraping, unauthorized access attempts, or apparent cloning, the matter should be reported urgently so it can be triaged as a security event rather than handled as an ordinary content issue.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Cancellation terms",
    href: "/legal/cancellation",
    description: "Review the cancellation path, no-refund boundaries, and how rendered digital work is treated commercially.",
  },
  {
    label: "Privacy notice",
    href: "/legal/privacy",
    description: "Read how support and billing requests interact with identity verification, data handling, and controlled disclosure.",
  },
  {
    label: "Singapore legal notice",
    href: "/legal/singapore",
    description: "See Singapore-facing PDPA, notification, and contact expectations for local users and residents.",
  },
  {
    label: "UK legal notice",
    href: "/legal/uk",
    description: "Review the UK-facing transparency, rights, and support handling language for UK visitors.",
  },
  {
    label: "Disclaimer",
    href: "/legal/disclaimer",
    description: "See the legal boundaries around public references, illustrative data, and proprietary analytical positioning.",
  },
  {
    label: "Security",
    href: "/security",
    description: "Review the public-facing security posture, owner-access expectations, and anti-abuse recommendations for the service.",
  },
];

type IntakeState = {
  fullName: string;
  email: string;
  issueType: string;
  region: string;
  orderReference: string;
  pageReference: string;
  details: string;
};

type SubmissionReceipt = IntakeState & {
  referenceCode: string;
  createdAt: number;
  status: "new" | "in_review" | "resolved";
};

const initialState: IntakeState = {
  fullName: "",
  email: "",
  issueType: "Billing or subscription",
  region: "Singapore",
  orderReference: "",
  pageReference: "",
  details: "",
};

export default function Support() {
  const [intake, setIntake] = useState<IntakeState>(initialState);
  const [receipt, setReceipt] = useState<SubmissionReceipt | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const preparedSummary = useMemo(() => {
    if (!receipt) {
      return "";
    }

    return [
      `Reference code: ${receipt.referenceCode}`,
      `Queue status: ${receipt.status}`,
      `Submitted: ${new Date(receipt.createdAt).toLocaleString()}`,
      `Support category: ${receipt.issueType}`,
      `Region: ${receipt.region}`,
      `Full name: ${receipt.fullName || "Not provided"}`,
      `Account email: ${receipt.email || "Not provided"}`,
      `Order or invoice reference: ${receipt.orderReference || "Not provided"}`,
      `Affected page or screen: ${receipt.pageReference || "Not provided"}`,
      `Issue details: ${receipt.details || "Not provided"}`,
    ].join("\n");
  }, [receipt]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        fullName: intake.fullName.trim(),
        email: intake.email.trim(),
        issueType: intake.issueType,
        region: intake.region,
        orderReference: intake.orderReference.trim(),
        pageReference: intake.pageReference.trim(),
        details: intake.details.trim(),
      };

      const result = await apiClient.support.submit.mutate(payload);
      setReceipt({
        ...payload,
        referenceCode: result.submission.referenceCode,
        createdAt: result.submission.createdAt,
        status: result.submission.status,
      });
      setIntake(initialState);
      toast.success("Support request submitted for manual review.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Support request could not be submitted.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!preparedSummary) {
      return;
    }

    await navigator.clipboard.writeText(preparedSummary);
    toast.success("Support receipt copied.");
  };

  const featurePanel = (
    <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
      <div className="rounded-[1.7rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0.02))] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.22)] md:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">Support intake flow</p>
            <h2 className="mt-4 font-display text-3xl text-white md:text-4xl">Submit a clear support brief into the review queue.</h2>
          </div>
          <MailCheck className="mt-1 h-5 w-5 text-[var(--signal-soft)]" />
        </div>

        <p className="mt-4 max-w-2xl text-sm leading-7 text-white/66 md:text-base">
          This intake flow stores the request for manual review so billing, privacy, report-delivery, and technical issues can be handled through a controlled support trail.
        </p>

        <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-2 text-sm text-white/72">
              <span>Full name</span>
              <Input
                value={intake.fullName}
                onChange={(event) => setIntake((current) => ({ ...current, fullName: event.target.value }))}
                placeholder="Name used for the order or account"
                required
              />
            </label>
            <label className="grid gap-2 text-sm text-white/72">
              <span>Account email</span>
              <Input
                type="email"
                required
                value={intake.email}
                onChange={(event) => setIntake((current) => ({ ...current, email: event.target.value }))}
                placeholder="you@example.com"
              />
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-2 text-sm text-white/72">
              <span>Issue category</span>
              <select
                value={intake.issueType}
                onChange={(event) => setIntake((current) => ({ ...current, issueType: event.target.value }))}
                className="border-input focus-visible:border-ring focus-visible:ring-ring/50 min-h-11 rounded-md border bg-transparent px-3 py-2 text-sm text-white outline-none focus-visible:ring-[3px]"
              >
                <option className="bg-[#0d1017] text-white">Billing or subscription</option>
                <option className="bg-[#0d1017] text-white">Upgrade or downgrade request</option>
                <option className="bg-[#0d1017] text-white">Cancellation request</option>
                <option className="bg-[#0d1017] text-white">Report delivery or access issue</option>
                <option className="bg-[#0d1017] text-white">Privacy or data request</option>
                <option className="bg-[#0d1017] text-white">Security or suspicious activity</option>
                <option className="bg-[#0d1017] text-white">Release-status or app review question</option>
                <option className="bg-[#0d1017] text-white">Website issue or broken link</option>
              </select>
            </label>
            <label className="grid gap-2 text-sm text-white/72">
              <span>Region</span>
              <select
                value={intake.region}
                onChange={(event) => setIntake((current) => ({ ...current, region: event.target.value }))}
                className="border-input focus-visible:border-ring focus-visible:ring-ring/50 min-h-11 rounded-md border bg-transparent px-3 py-2 text-sm text-white outline-none focus-visible:ring-[3px]"
              >
                <option className="bg-[#0d1017] text-white">Singapore</option>
                <option className="bg-[#0d1017] text-white">United Kingdom</option>
                <option className="bg-[#0d1017] text-white">Other region</option>
              </select>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="grid gap-2 text-sm text-white/72">
              <span>Order or invoice reference</span>
              <Input
                value={intake.orderReference}
                onChange={(event) => setIntake((current) => ({ ...current, orderReference: event.target.value }))}
                placeholder="Optional but helpful"
              />
            </label>
            <label className="grid gap-2 text-sm text-white/72">
              <span>Affected page or screen</span>
              <Input
                value={intake.pageReference}
                onChange={(event) => setIntake((current) => ({ ...current, pageReference: event.target.value }))}
                placeholder="URL, screen, or feature name"
              />
            </label>
          </div>

          <label className="grid gap-2 text-sm text-white/72">
            <span>Issue details</span>
            <Textarea
              required
              rows={6}
              value={intake.details}
              onChange={(event) => setIntake((current) => ({ ...current, details: event.target.value }))}
              placeholder="State what happened, when it happened, and what outcome you need."
              className="min-h-36"
            />
          </label>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button type="submit" disabled={isSubmitting} className="bg-[var(--signal-soft)] text-white hover:bg-[var(--signal)] disabled:cursor-not-allowed disabled:opacity-70">
              {isSubmitting ? "Submitting request..." : "Submit support request"}
            </Button>
            <p className="text-xs uppercase tracking-[0.24em] text-white/44">Stored for manual review • identity verification may still be required</p>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        <div className="rounded-[1.6rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.2)]">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">What to include</p>
          <div className="mt-5 space-y-4 text-sm leading-7 text-white/64">
            <div className="rounded-[1.1rem] border border-white/10 bg-white/3 p-4">
              <Sparkles className="h-4 w-4 text-[var(--signal-soft)]" />
              <p className="mt-3 text-white">State the exact request type.</p>
              <p className="mt-1">Billing, cancellation, privacy, report delivery, release-status, and technical issues should not be mixed into one vague note.</p>
            </div>
            <div className="rounded-[1.1rem] border border-white/10 bg-white/3 p-4">
              <ShieldAlert className="h-4 w-4 text-[var(--signal-soft)]" />
              <p className="mt-3 text-white">Use verifiable account details.</p>
              <p className="mt-1">If the request affects billing, protected reports, or privacy, the review process may pause until identity can be confirmed.</p>
            </div>
          </div>
        </div>

        <div className="rounded-[1.6rem] border border-[rgba(191,59,80,0.22)] bg-[linear-gradient(180deg,rgba(131,12,28,0.12),rgba(255,255,255,0.02))] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.22)]">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[var(--signal-soft)]">Submission receipt</p>
          <h3 className="mt-4 font-display text-2xl text-white">Stored request summary</h3>
          <p className="mt-3 text-sm leading-7 text-white/64">
            After submission, this panel shows the stored reference code and the request details you sent for manual review.
          </p>

          {receipt ? (
            <>
              <pre className="mt-5 whitespace-pre-wrap rounded-[1.1rem] border border-white/10 bg-[rgba(5,7,11,0.64)] p-4 text-sm leading-7 text-white/72">
                {preparedSummary}
              </pre>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button type="button" variant="outline" className="border-white/12 text-white/82 hover:bg-white/8 hover:text-white" onClick={handleCopy}>
                  Copy receipt
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-white/12 text-white/82 hover:bg-white/8 hover:text-white"
                  onClick={() => setReceipt(null)}
                >
                  Clear receipt
                </Button>
              </div>
            </>
          ) : (
            <div className="mt-5 rounded-[1.1rem] border border-dashed border-white/12 bg-[rgba(5,7,11,0.42)] p-4 text-sm leading-7 text-white/52">
              Submit the intake form to create a stored support request and receive a reference code.
            </div>
          )}
        </div>

        {user?.role === "admin" ? (
          <div className="rounded-[1.4rem] border border-white/10 bg-[rgba(255,255,255,0.03)] p-5">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--signal-soft)]">Admin access</p>
            <h3 className="mt-3 font-display text-2xl text-white">Internal support inbox</h3>
            <p className="mt-3 text-sm leading-7 text-white/64">
              You are signed in with admin access. Open the internal inbox to review stored submissions and update their review status.
            </p>
            <Button asChild className="mt-4 bg-[var(--signal-soft)] text-white hover:bg-[var(--signal)]">
              <a href="/support/inbox">Open internal inbox</a>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <PolicyLayout
      eyebrow="Support"
      title="A visible manual support path for billing, reports, privacy requests, and service issues."
      intro="Command Secrets provides this public support page so users can identify how plan administration, billing clarification, report-delivery issues, privacy requests, broken links, and security concerns should be handled when they need direct operational review."
      highlight="Support should reduce ambiguity, not add another silent loop."
      cards={cards}
      featurePanel={featurePanel}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
